import { useMemo } from 'react'

import EmptyState from 'common/components/emptyState'
import HighlightText from 'common/components/HighlightText'
import Table, { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import Pagination from 'common/components/table/pagination'

import {
  PaginatedProductCatalogSearchResultsFE,
  ProductCatalogSearchResultItemFE,
} from '../api/queries/useSearchProductCatalogQuery'

type Props = {
  searchResults: PaginatedProductCatalogSearchResultsFE | undefined
  isLoading: boolean
  currentPage: number
  currentPageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  searchValue: string
  onProductSelect: (productId: string, categoryId: string) => void
}

const SearchTable = ({
  searchResults,
  isLoading,
  currentPage,
  currentPageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onProductSelect,
}: Props) => {
  const columns: Column<ProductCatalogSearchResultItemFE>[] = useMemo(
    () => [
      {
        Header: 'Catalogue',
        accessor: 'ccnPath',
        Cell: ({ value, row }) => {
          const textToDisplay = `${value?.join(' / ') ?? ''} / ${
            row.original.name
          }`

          return (
            <BaseCell>
              <HighlightText text={textToDisplay} searchValue={searchValue} />
            </BaseCell>
          )
        },
      },
    ],
    [searchValue]
  )

  const items = searchResults?.data ?? []
  const totalCount = searchResults?.total ?? 0
  const pageCount = Math.ceil(totalCount / currentPageSize)

  if (!isLoading && items.length === 0 && totalCount === 0) {
    return (
      <EmptyState
        className="mt-6"
        title="No items found"
        description={
          searchResults
            ? 'Your search returned no items. Try a different term.'
            : 'Enter a search term above to find items in this catalog.'
        }
      />
    )
  }

  return (
    <>
      <Table<ProductCatalogSearchResultItemFE>
        columns={columns}
        onRowClick={(row) => onProductSelect(row.id, row.categoryId)}
        data={items}
        className="mt-6"
        disablePagination
        manualPagination
      />
      {totalCount > 0 && (
        <Pagination
          pageSize={currentPageSize}
          pageIndex={currentPage - 1}
          setPageSize={(size) => {
            onPageSizeChange(size)
            if (currentPage !== 1) {
              onPageChange(1)
            }
          }}
          gotoPage={(page) => onPageChange(page + 1)}
          canPreviousPage={currentPage > 1}
          previousPage={() => onPageChange(currentPage - 1)}
          nextPage={() => onPageChange(currentPage + 1)}
          pageCount={pageCount}
          canNextPage={currentPage < pageCount}
          totalRowCount={totalCount}
        />
      )}
    </>
  )
}

export default SearchTable
